import React, { useState, useEffect } from 'react';
import { toggleSidebar, UserMenu } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import MenuIcon from '@material-ui/icons/Menu';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import FeedbackIcon from '@material-ui/icons/Feedback';
import PrivacyIcon from '@material-ui/icons/Security';
import SettingsIcon from '@material-ui/icons/Settings';
import HelpIcon from '@material-ui/icons/Help';
import EmailIcon from '@material-ui/icons/Email';
import {
  AppBar,
  IconButton,
  ListItemIcon,
  makeStyles,
  MenuItem,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { useTour } from '@react-admin/ra-tour';

import {
  getCurrentEmployeeImageUrl,
  employeeAvatarImg,
  getZ3PConfigValue,
  setLocalStorage,
  getLocalStorage,
} from './helper';
import NotificationPanel from './components/NotificationPanel';
import BetaLabel from './components/BetaLabel';
import { getTour } from './tours/TourHelper';

const useStyles = makeStyles((theme) => ({
  menuButton: {
    marginRight: '0.5em',
    marginLeft: '-0.5em',
  },
  menuButtonIconOpen: {
    transition: theme.transitions.create(['transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    transform: 'rotate(180deg)',
  },
  menuButtonIconClosed: {
    transition: theme.transitions.create(['transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    transform: 'rotate(0deg)',
  },
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  betaLabel: {
    position: 'absolute',
    top: '4px',
    left: '132px',
  },
  topHeader: {
    minHeight: 64
  },
  headerColor: {
    backgroundColor: '#2b3642'
  }
}));

const settingsItem = (
  <MenuItem component="a" href="/#/settings">
    <ListItemIcon style={{ minWidth: 40 }}>
      <SettingsIcon />
    </ListItemIcon>
    Settings
  </MenuItem>
);

const feedbackItem = (
  <MenuItem
    component="a"
    target="_blank"
    href="https://share.hsforms.com/1RBj9dSmGTU6TYhEddHDBqg54ybc"
  >
    <ListItemIcon style={{ minWidth: 40 }}>
      <FeedbackIcon />
    </ListItemIcon>
    Feedback
  </MenuItem>
);

const faqItem = (
  <MenuItem
    component="a"
    target="_blank"
    href="https://knowledge.getzippity.com"
  >
    <ListItemIcon style={{ minWidth: 40 }}>
      <MenuBookIcon />
    </ListItemIcon>
    FAQs
  </MenuItem>
);

const contactusItem = (
  <MenuItem
    component="a"
    target="_blank"
    href="mailto:success@getzippity.com?subject=Admin Portal | Help Request"
  >
    <ListItemIcon style={{ minWidth: 40 }}>
      <EmailIcon />
    </ListItemIcon>
    Contact Support
  </MenuItem>
);

const privacyPolicyItem = (
  <MenuItem component="a" target="_blank" href="/#/privacy-policy">
    <ListItemIcon style={{ minWidth: 40 }}>
      <PrivacyIcon />
    </ListItemIcon>
    Privacy Policy
  </MenuItem>
);

const tourItem = (handleShowTour) => (
  <MenuItem component="div">
    <ListItemIcon style={{ minWidth: 40 }} onClick={handleShowTour}>
      <MenuBookIcon />
    </ListItemIcon>
    <span style={{ width: '100%' }} onClick={handleShowTour}>
      Quick Start Guide
    </span>
  </MenuItem>
);

const businessSettingsItem = (
  <MenuItem component="a" href="/#/business-configuration-settings">
    <ListItemIcon style={{ minWidth: 40 }}>
      <SettingsIcon />
    </ListItemIcon>
    Business Settings (Zippity only)
  </MenuItem>
);

const instantSignupItem = (
  <MenuItem component="a" href="/#/pro-list/create">
    <ListItemIcon style={{ minWidth: 40 }}>
      <SettingsIcon />
    </ListItemIcon>
    Instant Signup (Zippity only)
  </MenuItem>
);

const proListItem = (
  <MenuItem component="a" href="/#/pro-list">
    <ListItemIcon style={{ minWidth: 40 }}>
      <SettingsIcon />
    </ListItemIcon>
    Pro List (Zippity only)
  </MenuItem>
);

const employeeAvatar = () =>
  employeeAvatarImg(getCurrentEmployeeImageUrl(), 30, 2.1);

const MyUserMenu = ({ handleShowTour, ...props }) => {
  const user = localStorage.getItem('user');
  let username = null;
  if (user) {
    username = JSON.parse(user)?.username;
  }
  return (
    <UserMenu data-tour-id="profile-icon" icon={employeeAvatar()} {...props}>
      {settingsItem}
      {(username?.includes('@zippitycars.com') ||
        username?.includes('@getzippity.com') ||
        username?.includes('@zippityinfra.com') ||
        username?.includes('@simpletire.com')) && [
        businessSettingsItem,
        instantSignupItem,
        proListItem,
      ]}
    </UserMenu>
  );
};

const HelperMenu = ({ handleShowTour }) => {
  return (
    <UserMenu label="Help" icon={<HelpIcon />}>
      {handleShowTour && tourItem(handleShowTour)}
      {faqItem}
      {feedbackItem}
      {contactusItem}
      {privacyPolicyItem}
    </UserMenu>
  );
};

const MyAppBar = (props) => {
  const { open, logout } = props;
  const classes = useStyles(props);
  const [_, { start }] = useTour();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isQuotePage, setIsQuotePage] = useState(false);
  useEffect(() => {
    setIsQuotePage(location.pathname === '/quote');
  }, [location]);
  const menus = getZ3PConfigValue('menus');
  const tour = getTour(menus);

  const handleShowTour = () => {
    start(tour);
  };

  useEffect(() => {
    // Automatically show tour when page loads, if user hasn't seen tour before.
    const tourHasBeenShown = getLocalStorage('showTour');
    const hasToken = !!getLocalStorage('token');
    if (!tourHasBeenShown && tour && hasToken) {
      handleShowTour();
      setLocalStorage('showTour', true);
    }
  }, []);

  return (
    <AppBar {...props} className={classes.headerColor}>
      <Toolbar variant="dense" {...props} className={classes.topHeader}>
        <IconButton
          color="inherit"
          onClick={() => dispatch(toggleSidebar())}
          className={classes.menuButton}
        >
          <MenuIcon
            classes={{
              root: open
                ? classes.menuButtonIconOpen
                : classes.menuButtonIconClosed,
            }}
          />
        </IconButton>
        <Typography
          variant="h6"
          color="inherit"
          className={classes.title}
          id="react-admin-title"
        />
        {isQuotePage && <BetaLabel classAttr={classes.betaLabel} />}

        <HelperMenu handleShowTour={tour ? handleShowTour : null} />

        <NotificationPanel />
        <div data-tour-id="profile-icon">
          <MyUserMenu logout={logout} />
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default MyAppBar;
